import React, { useContext, useEffect, useState } from 'react';
import { bool, func } from 'prop-types';

// Nucleus Components
import { Button } from '@thd-olt-component-react/core-ui';
import { useStore } from '@thd-nucleus/experience-context';

// Local Components
import { ReservationContext } from '../../context/reservation-context';
import { DateTimePicker } from '../date-time-picker/date-time-picker';
import helper from '../../utils/helper';
import { FORM_FIELD_MESSAGE } from '../../constants/messages';
import { DEFAULT_STORE_HOURS } from '../../constants/constants';

import './timeslot-form.scss';

export const TimeslotForm = ({ checkAvailability, disabled }) => {
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [formValidationClass, setFormValidationClass] = useState('');
  const [validationClasses, setValidationClasses] = useState({
    pickupDate: '',
    pickupTime: '',
    dropoffDate: '',
    dropoffTime: ''
  });

  const { storeHours = DEFAULT_STORE_HOURS, storeId } = useStore();
  const [reservationState, dispatch] = useContext(ReservationContext);
  const { reservation, activeSection } = reservationState;

  const errorMessageHandler = (name) => {
    if (
      (name === 'pickupTime' || name === 'dropoffDate' || name === 'dropoffTime')
      && !reservation?.pickupDate
    ) {
      setFormMessage(FORM_FIELD_MESSAGE.pickupDate);
      setValidationClasses({ ...validationClasses, pickupDate: 'form-input--error' });
      setFormValidationClass('error');
    } else if ((name === 'dropoffDate' || name === 'dropoffTime') && !reservation.pickupTime) {
      setFormMessage(FORM_FIELD_MESSAGE.pickupTime);
      setValidationClasses({ ...validationClasses, pickupTime: 'form-input--error' });
      setFormValidationClass('error');
    } else if (name === 'dropoffTime' && !reservation?.dropoffDate) {
      setFormMessage(FORM_FIELD_MESSAGE.dropoffDate);
      setValidationClasses({ ...validationClasses, dropoffDate: 'form-input--error' });
      setFormValidationClass('error');
    }
  };

  const handleChange = ({ value, field }) => {
    if (field === 'pickupDate') {
      dispatch({ type: 'SET_PICKUP_DATE', payload: value });
    } else if (field === 'pickupTime') {
      dispatch({ type: 'SET_PICKUP_TIME', payload: value });
    } else if (field === 'dropoffDate') {
      dispatch({ type: 'SET_DROPOFF_DATE', payload: value });
    } else if (field === 'dropoffTime') {
      dispatch({ type: 'SET_DROPOFF_TIME', payload: value });
    }
  };

  const handleClick = (input) => {
    errorMessageHandler(input);
  };

  const clearOldData = (missingFields) => {
    if (reservation?.pickupDate && missingFields.includes('pickupDate')) {
      dispatch({ type: 'SET_PICKUP_DATE', payload: '' });
    }
    if (reservation?.pickupTime && missingFields.includes('pickupTime')) {
      dispatch({ type: 'SET_PICKUP_TIME', payload: '' });
    }
    if (reservation?.dropoffDate && missingFields.includes('dropoffDate')) {
      dispatch({ type: 'SET_DROPOFF_DATE', payload: '' });
    }
    if (reservation?.dropoffTime && missingFields.includes('dropoffTime')) {
      dispatch({ type: 'SET_DROPOFF_TIME', payload: '' });
    }
  };

  useEffect(() => {
    const validation = helper.triggerFormValidations({
      storeNbr: storeId,
      rentalStoreHours: storeHours,
      reservation
    });
    setFormValidationClass('success');
    setValidationClasses({
      pickupDate: !validation.missingFields.includes('pickupDate') ? 'form-input--success' : '',
      pickupTime: !validation.missingFields.includes('pickupTime') ? 'form-input--success' : '',
      dropoffDate: !validation.missingFields.includes('dropoffDate') ? 'form-input--success' : '',
      dropoffTime: !validation.missingFields.includes('dropoffTime') ? 'form-input--success' : ''
    });
    if (validation.missingFields.length !== 0) {
      setFormMessage(FORM_FIELD_MESSAGE[validation?.missingFields[0]]);
      clearOldData(validation.missingFields);
    } else {
      setFormMessage('');
    }
    if (activeSection !== 'DaysAndTimes') {
      dispatch({ type: 'SET_ACTIVE_SECTION', payload: 'DaysAndTimes' });
    }
    if (buttonEnabled !== validation.valid) {
      setButtonEnabled(validation.valid);
    }
  }, [reservation]);

  return (
    <div className="timeslot-form">
      <div
        className={`timeslot-input-form ${formValidationClass}`}
        data-testid="timeslot-input-form"
      >
        <DateTimePicker
          disabled={disabled}
          label="Pickup"
          field="pickup"
          validationClasses={validationClasses}
          handleClick={handleClick}
          handleChange={handleChange}
        />
        <DateTimePicker
          disabled={disabled}
          label="Drop-Off"
          field="dropoff"
          validationClasses={validationClasses}
          handleClick={handleClick}
          handleChange={handleChange}
          calendarEnabled={(!!reservation?.pickupDate && !!reservation?.pickupTime)}
        />
        {!disabled && formMessage && (
          <div
            className={`form-message ${formValidationClass}__message`}
            data-testid="form-message"
          >
            {formMessage}
          </div>
        )}
        <div className="center">
          <div className="search-button">
            <Button
              data-testid="check-availability"
              disabled={!buttonEnabled}
              onClick={checkAvailability}
            >
              Check Availability
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

TimeslotForm.propTypes = {
  disabled: bool,
  checkAvailability: func.isRequired
};

TimeslotForm.defaultProps = {
  disabled: false
};
